/**
 * Modal for capturing a reason when putting a PO line on hold.
 *
 * Follows the same pattern as AcceptDateModal / DeclineDateModal.
 */
import { useState } from 'react';
import { X, PauseCircle, Loader2 } from 'lucide-react';
import { Card } from '../ui/card';

interface HoldReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  lineNumber: number | string;
  partNumber: string;
}

export function HoldReasonModal({
  isOpen,
  onClose,
  onSubmit,
  lineNumber,
  partNumber,
}: HoldReasonModalProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(reason.trim());
      setReason('');
      onClose();
    } catch {
      // error toast handled by caller
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <PauseCircle className="h-5 w-5 text-red-600" />
            <h3 className="text-lg font-semibold">Put Line On Hold</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-full transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="text-sm text-muted-foreground">
            Line <span className="font-medium text-foreground">{lineNumber}</span>
            {partNumber && <> &mdash; Part <span className="font-medium text-foreground">{partNumber}</span></>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for putting this line on hold..."
              className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
              rows={3}
              required
              autoFocus
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm font-medium transition-colors"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !reason.trim()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <PauseCircle className="h-4 w-4" />
                  Put On Hold
                </>
              )}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
